import { capitalize } from './utils/strings'

export type Language = {
  label: string
  value: string
}

export type LanguageCode = (typeof supportedLanguageCodes)[number]

export const supportedLanguageCodes = ['ca', 'de', 'en', 'es'] as const

export const getNativeName: (code: string) => string = (code: string): string => {
  try {
    const languageNames = new Intl.DisplayNames([code], {
      type: 'language'
    })
    return capitalize(languageNames.of(code) || code)
  } catch {
    return code
  }
}

export const supportedLanguages = supportedLanguageCodes.map(code => ({
  label: capitalize(getNativeName(code)),
  value: code
}))

export const getName: (code: string, displayLanguage?: string) => string = (
  code: string,
  displayLanguage: string = 'en'
): string => {
  try {
    const languageNames = new Intl.DisplayNames([displayLanguage], {
      type: 'language'
    })

    return capitalize(languageNames.of(code) || code)
  } catch {
    return code
  }
}

const googleTranslateLanguageCodes: string[] = [
  'af', // Afrikaans
  'sq', // Albanian
  'am', // Amharic
  'ar', // Arabic
  'hy', // Armenian
  'az', // Azerbaijani
  'eu', // Basque
  'be', // Belarusian
  'bn', // Bengali
  'bs', // Bosnian
  'bg', // Bulgarian
  'ca', // Catalan
  'ceb', // Cebuano
  'ny', // Chichewa
  'zh-CN', // Chinese (Simplified)
  'zh-TW', // Chinese (Traditional)
  'co', // Corsican
  'hr', // Croatian
  'cs', // Czech
  'da', // Danish
  'nl', // Dutch
  'en', // English
  'eo', // Esperanto
  'et', // Estonian
  'tl', // Filipino (Tagalog)
  'fi', // Finnish
  'fr', // French
  'fy', // Frisian
  'gl', // Galician
  'ka', // Georgian
  'de', // German
  'el', // Greek
  'gu', // Gujarati
  'ht', // Haitian Creole
  'ha', // Hausa
  'haw', // Hawaiian
  'iw', // Hebrew (iw is legacy code, he is also used)
  'hi', // Hindi
  'hmn', // Hmong
  'hu', // Hungarian
  'is', // Icelandic
  'ig', // Igbo
  'id', // Indonesian
  'ga', // Irish
  'it', // Italian
  'ja', // Japanese
  'jw', // Javanese
  'kn', // Kannada
  'kk', // Kazakh
  'km', // Khmer
  'rw', // Kinyarwanda
  'ko', // Korean
  'ku', // Kurdish
  'ky', // Kyrgyz
  'lo', // Lao
  'la', // Latin
  'lv', // Latvian
  'lt', // Lithuanian
  'lb', // Luxembourgish
  'mk', // Macedonian
  'mg', // Malagasy
  'ms', // Malay
  'ml', // Malayalam
  'mt', // Maltese
  'mi', // Maori
  'mr', // Marathi
  'mn', // Mongolian
  'my', // Myanmar (Burmese)
  'ne', // Nepali
  'no', // Norwegian
  'or', // Odia (Oriya)
  'ps', // Pashto
  'fa', // Persian
  'pl', // Polish
  'pt', // Portuguese
  'pa', // Punjabi
  'ro', // Romanian
  'ru', // Russian
  'sm', // Samoan
  'gd', // Scots Gaelic
  'sr', // Serbian
  'st', // Sesotho
  'sn', // Shona
  'sd', // Sindhi
  'si', // Sinhala
  'sk', // Slovak
  'sl', // Slovenian
  'so', // Somali
  'es', // Spanish
  'su', // Sundanese
  'sw', // Swahili
  'sv', // Swedish
  'tg', // Tajik
  'ta', // Tamil
  'te', // Telugu
  'th', // Thai
  'tr', // Turkish
  'uk', // Ukrainian
  'ur', // Urdu
  'uz', // Uzbek
  'vi', // Vietnamese
  'cy', // Welsh
  'xh', // Xhosa
  'yi', // Yiddish
  'yo', // Yoruba
  'zu' // Zulu
]

export const getAllCodes: () => string[] = () => googleTranslateLanguageCodes
